import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Paper,
  Chip,
  Divider,
  IconButton,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Alert,
  Snackbar,
  MenuItem
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Home as HomeIcon,
  Church as ChurchIcon,
  Favorite as FavoriteIcon,
  Payments as PaymentsIcon,
  Notes as NotesIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import axios from '../utils/axios';
import OCIAForm from './OCIAForm';
import {
  MARITAL_STATUS_OPTIONS,
  OFFICIATING_AUTHORITY_OPTIONS,
  BAPTIZED_OPTIONS,
  SACRAMENT_OPTIONS,
  INTENT_OPTIONS,
  MARITAL_NEEDS_MARRIAGE,
  MARITAL_NEEDS_PARTNER,
  labelOf,
  ociaFullName,
  formatPhoneNumber,
  formatAddress
} from '../utils/ociaFields';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '');
const fmtMoney = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const yesNo = (b) => (b ? 'Yes' : 'No');

const Field = ({ label, value }) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>{value || '—'}</Typography>
  </Grid>
);

const SectionCard = ({ icon, title, children, action }) => (
  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon}
        <Typography variant="h6">{title}</Typography>
      </Box>
      {action}
    </Box>
    <Divider sx={{ mb: 2 }} />
    {children}
  </Paper>
);

const OCIADetails = ({ registration, open, onClose, onUpdate }) => {
  const [reg, setReg] = useState(registration);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [txn, setTxn] = useState({ amount: '', method: 'cash', reference: '' });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    setReg(registration);
  }, [registration]);

  if (!reg) return null;

  const totalPaid = (reg.payment?.transactions || []).reduce((sum, t) => sum + (t.amount || 0), 0);
  const balance = Math.max(0, (reg.payment?.amount || 0) - totalPaid);
  const status = reg.maritalStatus?.status;

  const refresh = (data) => {
    if (data) setReg(data);
    if (onUpdate) onUpdate();
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    try {
      const res = await axios.post(`/ocia/${reg._id}/notes`, { content: noteText.trim() });
      setNoteText('');
      setSuccess('Note added');
      refresh(res.data);
    } catch (e) {
      setError('Failed to add note');
    }
  };

  const handleAddTransaction = async () => {
    const amount = parseFloat(txn.amount);
    if (!amount || amount <= 0) {
      setError('Enter a valid transaction amount');
      return;
    }
    try {
      const res = await axios.post(`/ocia/${reg._id}/payment/transaction`, {
        amount,
        method: txn.method,
        reference: txn.reference
      });
      setTxn({ amount: '', method: 'cash', reference: '' });
      setSuccess('Payment recorded');
      refresh(res.data);
    } catch (e) {
      setError('Failed to record payment');
    }
  };

  const handleToggleHardship = async () => {
    try {
      const newStatus = reg.payment?.status === 'hardship' ? 'open' : 'hardship';
      const res = await axios.patch(`/ocia/${reg._id}/payment`, { status: newStatus });
      setSuccess(`Marked as ${newStatus}`);
      refresh(res.data);
    } catch (e) {
      setError('Failed to update payment status');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`/ocia/${reg._id}`);
      setConfirmDelete(false);
      if (onUpdate) onUpdate();
      onClose();
    } catch (e) {
      setError('Failed to delete registration');
      setConfirmDelete(false);
    }
  };

  const renderPreviousMarriages = (list) => (
    (list || []).map((pm, i) => (
      <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1, bgcolor: 'action.hover' }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Previous Marriage {i + 1}</Typography>
        <Grid container spacing={1}>
          <Field label="Name" value={pm.name} />
          <Field label="Religious Affiliation" value={pm.affiliation} />
          <Field label="Date of Marriage" value={fmtDate(pm.dateOfMarriage)} />
          <Field label="Place of Marriage" value={pm.placeOfMarriage} />
          <Field label="Officiating Authority" value={labelOf(OFFICIATING_AUTHORITY_OPTIONS, pm.officiatingAuthority)} />
        </Grid>
      </Paper>
    ))
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h5" component="span">{ociaFullName(reg)}</Typography>
            <Chip
              size="small"
              sx={{ ml: 2, fontWeight: 600 }}
              label={reg.payment?.status ? reg.payment.status.charAt(0).toUpperCase() + reg.payment.status.slice(1) : 'Open'}
              color={reg.payment?.status === 'paid' ? 'success' : reg.payment?.status === 'hardship' ? 'info' : 'warning'}
            />
          </Box>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {/* Identity */}
          <SectionCard icon={<PersonIcon color="primary" />} title="Personal Information">
            <Grid container spacing={2}>
              <Field label="Date Completed" value={fmtDate(reg.dateFilled)} />
              <Field label="First Name" value={reg.firstName} />
              <Field label="Middle Name" value={reg.middleName} />
              <Field label="Last Name" value={reg.lastName} />
              <Field label="Maiden Name" value={reg.maidenName} />
              <Field label="Date of Birth" value={fmtDate(reg.dateOfBirth)} />
              <Field
                label="Place of Birth"
                value={[reg.placeOfBirth?.city, reg.placeOfBirth?.stateProvince, reg.placeOfBirth?.postalCode, reg.placeOfBirth?.country].filter(Boolean).join(', ')}
              />
              <Field label="Father's Name" value={reg.fatherName} />
              <Field label="Mother's Name" value={reg.motherName} />
              <Field label="Mother's Maiden Name" value={reg.motherMaidenName} />
            </Grid>
          </SectionCard>

          {/* Contact */}
          <SectionCard icon={<HomeIcon color="primary" />} title="Contact">
            <Grid container spacing={2}>
              <Field label="Email" value={reg.email} />
              <Field label="Cell Phone" value={formatPhoneNumber(reg.cellPhone)} />
              <Field label="Occupation" value={reg.occupation} />
              <Field label="Student?" value={yesNo(reg.isStudent)} />
              <Field label="Home Address" value={formatAddress(reg.homeAddress)} />
              {reg.isStudent && <Field label="School Address" value={formatAddress(reg.schoolAddress)} />}
            </Grid>
          </SectionCard>

          {/* Religious history */}
          <SectionCard icon={<ChurchIcon color="primary" />} title="Religious History">
            <Grid container spacing={2}>
              <Field label="Present Religious Affiliation" value={reg.religiousHistory?.presentAffiliation} />
              <Field label="Ever Baptized?" value={labelOf(BAPTIZED_OPTIONS, reg.religiousHistory?.everBaptized)} />
            </Grid>
            {reg.religiousHistory?.everBaptized === 'yes' && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Baptism Details</Typography>
                <Grid container spacing={2}>
                  <Field label="Denomination" value={reg.religiousHistory?.baptism?.denomination} />
                  <Field label="Date or Approx. Age" value={reg.religiousHistory?.baptism?.dateOrAge} />
                  <Field label="Baptismal Name" value={reg.religiousHistory?.baptism?.baptismalName} />
                  <Field label="Place of Baptism (Church)" value={reg.religiousHistory?.baptism?.churchName} />
                  <Field label="Church Address" value={reg.religiousHistory?.baptism?.churchAddress} />
                  <Field label="Location" value={reg.religiousHistory?.baptism?.location} />
                </Grid>
              </Box>
            )}
            {(reg.religiousHistory?.catholicSacramentsReceived || []).length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">Sacraments Already Received</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                  {reg.religiousHistory.catholicSacramentsReceived.map(s => (
                    <Chip key={s} size="small" label={labelOf(SACRAMENT_OPTIONS, s)} />
                  ))}
                </Box>
              </Box>
            )}
          </SectionCard>

          {/* Marital status */}
          <SectionCard icon={<FavoriteIcon color="primary" />} title="Marital Status">
            <Grid container spacing={2}>
              <Field label="Status" value={labelOf(MARITAL_STATUS_OPTIONS, status)} />
            </Grid>

            {MARITAL_NEEDS_PARTNER.includes(status) && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {status === 'engaged' ? 'Fiancé(e)' : 'Spouse'}
                </Typography>
                <Grid container spacing={2}>
                  <Field label="Name" value={reg.maritalStatus?.partner?.name} />
                  <Field label="Religious Affiliation" value={reg.maritalStatus?.partner?.affiliation} />
                  <Field label="Previously Married?" value={yesNo(reg.maritalStatus?.partner?.marriedBefore)} />
                </Grid>
              </Box>
            )}

            {MARITAL_NEEDS_MARRIAGE.includes(status) && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Marriage Details</Typography>
                <Grid container spacing={2}>
                  <Field label="Date of Marriage" value={fmtDate(reg.maritalStatus?.marriage?.dateOfMarriage)} />
                  <Field label="Place of Marriage" value={reg.maritalStatus?.marriage?.placeOfMarriage} />
                  <Field label="Officiating Authority" value={labelOf(OFFICIATING_AUTHORITY_OPTIONS, reg.maritalStatus?.marriage?.officiatingAuthority)} />
                </Grid>
              </Box>
            )}

            {status === 'widowed' && (
              <Grid container spacing={2} sx={{ mt: 0 }}>
                <Field label="Previously married to someone still living?" value={yesNo(reg.maritalStatus?.widowedPreviousSpouseStillLiving)} />
              </Grid>
            )}

            {reg.maritalStatus?.registrant?.marriedBefore && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Registrant's Previous Marriage(s)</Typography>
                {renderPreviousMarriages(reg.maritalStatus?.registrant?.previousMarriages)}
              </Box>
            )}

            {reg.maritalStatus?.partner?.marriedBefore && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {status === 'engaged' ? "Fiancé(e)'s Previous Marriage(s)" : "Spouse's Previous Marriage(s)"}
                </Typography>
                {renderPreviousMarriages(reg.maritalStatus?.partner?.previousMarriages)}
              </Box>
            )}
          </SectionCard>

          {/* Faith journey */}
          <SectionCard icon={<ChurchIcon color="primary" />} title="Faith Journey">
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">What or who has led you to want to know more about the Catholic faith?</Typography>
                <Typography variant="body1">{reg.faithJourney?.whatLedYou || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Religious education received (as a child and as an adult)</Typography>
                <Typography variant="body1">{reg.faithJourney?.religiousEducationReceived || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Contact with the Catholic Church to date</Typography>
                <Typography variant="body1">{reg.faithJourney?.contactWithChurch || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Questions or concerns about the Catholic Church</Typography>
                <Typography variant="body1">{reg.faithJourney?.questionsConcerns || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Present feelings about joining</Typography>
                <Typography variant="body1">{labelOf(INTENT_OPTIONS, reg.intent)}</Typography>
              </Box>
            </Stack>
          </SectionCard>

          {/* Payment */}
          <SectionCard
            icon={<PaymentsIcon color="primary" />}
            title="Payment"
            action={
              <Button size="small" variant="outlined" onClick={handleToggleHardship}>
                {reg.payment?.status === 'hardship' ? 'Remove Hardship' : 'Mark Hardship'}
              </Button>
            }
          >
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Field label="Total Due" value={fmtMoney(reg.payment?.amount)} />
              <Field label="Total Paid" value={fmtMoney(totalPaid)} />
              <Field label="Balance" value={fmtMoney(balance)} />
              <Field label="Method" value={reg.payment?.method} />
            </Grid>

            {(reg.payment?.transactions || []).length > 0 && (
              <TableContainer sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Method</TableCell>
                      <TableCell>Reference</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reg.payment.transactions.map((t, i) => (
                      <TableRow key={i}>
                        <TableCell>{fmtDate(t.date)}</TableCell>
                        <TableCell>{fmtMoney(t.amount)}</TableCell>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{t.method}</TableCell>
                        <TableCell>{t.reference || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Typography variant="subtitle2" sx={{ mb: 1 }}>Record a Payment</Typography>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={6} sm={3}>
                <TextField
                  label="Amount"
                  type="number"
                  size="small"
                  fullWidth
                  value={txn.amount}
                  onChange={(e) => setTxn({ ...txn, amount: e.target.value })}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  select
                  label="Method"
                  size="small"
                  fullWidth
                  value={txn.method}
                  onChange={(e) => setTxn({ ...txn, method: e.target.value })}
                >
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="check">Check</MenuItem>
                  <MenuItem value="online">Online</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={8} sm={4}>
                <TextField
                  label="Reference (optional)"
                  size="small"
                  fullWidth
                  value={txn.reference}
                  onChange={(e) => setTxn({ ...txn, reference: e.target.value })}
                />
              </Grid>
              <Grid item xs={4} sm={2}>
                <Button variant="contained" fullWidth onClick={handleAddTransaction}>Add</Button>
              </Grid>
            </Grid>
          </SectionCard>

          {/* Signature */}
          {(reg.signature?.name || reg.signature?.date) && (
            <SectionCard icon={<EditIcon color="primary" />} title="Signature">
              <Grid container spacing={2}>
                <Field label="Signed By" value={reg.signature?.name} />
                <Field label="Date" value={fmtDate(reg.signature?.date)} />
              </Grid>
            </SectionCard>
          )}

          {/* Notes */}
          <SectionCard icon={<NotesIcon color="primary" />} title="Notes">
            <Stack spacing={1} sx={{ mb: 2 }}>
              {(reg.notes || []).length === 0 && (
                <Typography variant="body2" color="text.secondary">No notes yet.</Typography>
              )}
              {(reg.notes || []).map((n, i) => (
                <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="body2">{n.content}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {n.author?.name} · {fmtDate(n.date)}
                  </Typography>
                </Paper>
              ))}
            </Stack>
            <Stack direction="row" spacing={1}>
              <TextField
                fullWidth
                size="small"
                placeholder="Add a note"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
              />
              <Button variant="contained" onClick={handleAddNote}>Add</Button>
            </Stack>
          </SectionCard>

          {/* Activity */}
          <SectionCard icon={<HistoryIcon color="primary" />} title="Activity Log">
            <Stack spacing={1}>
              {(reg.activity || []).slice().reverse().map((a, i) => (
                <Box key={i}>
                  <Typography variant="body2">
                    <strong>{a.action}</strong>{a.details ? ` — ${a.details}` : ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {a.user?.name || 'System'} · {fmtDate(a.date)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </SectionCard>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
          <Button color="error" startIcon={<DeleteIcon />} onClick={() => setConfirmDelete(true)}>
            Delete
          </Button>
          <Box>
            <Button onClick={onClose} sx={{ mr: 1 }}>Close</Button>
            <Button variant="contained" startIcon={<EditIcon />} onClick={() => setEditOpen(true)}>
              Edit
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Edit form (reuses OCIAForm in edit mode) */}
      <OCIAForm
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSuccess={() => { setEditOpen(false); if (onUpdate) onUpdate(); }}
        registration={reg}
        currentSchoolYear={reg.schoolYear}
      />

      {/* Confirm delete */}
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Delete Registration?</DialogTitle>
        <DialogContent>
          <Typography>This will permanently delete {ociaFullName(reg)}'s OCIA registration. This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess(null)}>
        <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </>
  );
};

export default OCIADetails;
